import { BellOutlined, CheckCircleOutlined, FolderOutlined } from '@ant-design/icons';
import { Alert, Skeleton } from 'antd';
import React from 'react';

import { WarningIcon } from '@/icons';
import type { EscalationHistory, TierLabel } from '@/models/escalation.model';

import { NotificationHistoryRow } from './NotificationHistoryRow';

export interface NotificationHistoryPanelProps {
  history: EscalationHistory | null;
  loading: boolean;
  error: boolean;
  /** When false the panel is not rendered — gates visibility on can_create_weekly_status. */
  canView: boolean;
}

type TierState = {
  borderColor: string;
  iconBg: string;
  iconColor: string;
  icon: React.ReactNode;
  title: string;
};

const EMPTY_STATE: TierState = {
  borderColor: 'var(--border-default)',
  iconBg: 'var(--bg-subtle)',
  iconColor: 'var(--text-secondary)',
  icon: <BellOutlined />,
  title: 'No escalation this cycle',
};

const TIER_STATE: Record<TierLabel, TierState> = {
  PM: {
    borderColor: 'var(--tag-warning-border)',
    iconBg: 'var(--tag-warning-bg)',
    iconColor: 'var(--tag-warning-text)',
    icon: <WarningIcon />,
    title: 'PM notified',
  },
  DL: {
    borderColor: 'var(--tag-warning-border)',
    iconBg: 'var(--tag-warning-bg)',
    iconColor: 'var(--tag-warning-text)',
    icon: <WarningIcon />,
    title: 'Delivery Lead notified',
  },
  OWNER: {
    borderColor: 'var(--tag-error-border)',
    iconBg: 'var(--tag-error-bg)',
    iconColor: 'var(--tag-error-text)',
    icon: <WarningIcon />,
    title: 'Escalated to Deal Owner',
  },
  CL: {
    borderColor: 'var(--tag-error-border)',
    iconBg: 'var(--tag-error-bg)',
    iconColor: 'var(--tag-error-text)',
    icon: <WarningIcon />,
    title: 'Escalated to Client Lead',
  },
  CONFIRMED: {
    borderColor: 'var(--tag-success-border)',
    iconBg: 'var(--tag-success-bg)',
    iconColor: 'var(--tag-success-text)',
    icon: <CheckCircleOutlined />,
    title: 'Update confirmed',
  },
};

export function NotificationHistoryPanel({
  history,
  loading,
  error,
  canView,
}: Readonly<NotificationHistoryPanelProps>): React.ReactElement | null {
  if (!canView) {
    return null;
  }

  if (loading) {
    return (
      <div className="notification-history-panel" data-testid="notification-history-panel">
        <div className="notification-history-panel__header">
          <Skeleton.Avatar size={36} shape="square" active />
          <Skeleton active title={{ width: '60%' }} paragraph={{ rows: 1, width: '40%' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notification-history-panel" data-testid="notification-history-panel">
        <div
          className="notification-history-panel__header"
          style={{ padding: 'var(--space-4) var(--space-5)' }}
        >
          <Alert
            type="error"
            message="Could not load notification history"
            showIcon
            style={{ width: '100%' }}
          />
        </div>
      </div>
    );
  }

  const latestTier = (history?.latestTier as TierLabel | null) ?? null;
  const state = latestTier ? TIER_STATE[latestTier] : EMPTY_STATE;
  const entries = history?.entries ?? [];

  return (
    <div
      className="notification-history-panel"
      data-testid="notification-history-panel"
      style={{ borderTop: `4px solid ${state.borderColor}` }}
    >
      {/* Header — icon square + title + subtitle */}
      <div className="notification-history-panel__header">
        <div
          className="notification-history-panel__icon"
          style={{ background: state.iconBg, color: state.iconColor }}
        >
          {state.icon}
        </div>
        <div className="notification-history-panel__header-text">
          <span className="notification-history-panel__title">{state.title}</span>
          <span className="notification-history-panel__subtitle">
            <FolderOutlined />
            Notification History
          </span>
        </div>
      </div>

      {/* Body — inset rows section */}
      <div className="notification-history-panel__body">
        <div className="notification-history-panel__inset">
          <p className="notification-history-panel__section-label">Notification History</p>

          {entries.length === 0 ? (
            <p className="notification-history-panel__empty">
              No notifications sent for this project
            </p>
          ) : (
            <div className="notification-history-panel__list">
              {entries.map((entry, idx) => (
                <NotificationHistoryRow
                  key={`${entry.tier}-${entry.notifiedDate}-${idx}`}
                  entry={entry}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
